import { BrowserRouter as Router, Routes, Route, Navigate, Link } from "react-router-dom";
import { Login } from "./pages/Login";
import { Signup } from "./pages/Signup";
import { Dashboard } from "./pages/Dashboard";
import { Landing } from "./pages/Landing";
import { About } from "./pages/About";
import { Methodology } from "./pages/Methodology";
import { FAQ } from "./pages/FAQ";
import { Privacy } from "./pages/Privacy";
import { Terms } from "./pages/Terms";
import { AccountPricing } from "./pages/AccountPricing";
import { HowItWorks } from "./pages/HowItWorks";
import { PaymentSuccess } from "./pages/PaymentSuccess";
import { PaymentCancel } from "./pages/PaymentCancel";
import Analytics from "./pages/Analytics";
import { Groups } from "./pages/Groups";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { useAuth } from "./hooks/useAuth";
import { isProOrHigher } from "./utils/tierHelpers";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { Navbar } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import { Lock } from "lucide-react";

/* ---------------- Groups Paywall ---------------- */
const GroupsPaywall = () => {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f9fafb',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '600px',
        width: '100%',
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '3rem',
        textAlign: 'center',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          background: 'linear-gradient(90deg, #2563eb, #4f46e5)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          <Lock size={40} color="white" />
        </div>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: '700',
          color: '#111827',
          marginBottom: '1rem'
        }}>
          Groups is a Pro feature
        </h1>
        <p style={{
          fontSize: '1.125rem',
          color: '#6b7280',
          lineHeight: '1.75',
          marginBottom: '2rem'
        }}>
          Upgrade to Pro to combine reporting entities and compare totals across cycles.
        </p>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          alignItems: 'center'
        }}>
          <Link
            to="/account/pricing"
            style={{
              display: 'inline-block',
              padding: '0.875rem 2rem',
              background: 'linear-gradient(90deg, #2563eb, #4f46e5)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'transform 0.2s'
            }}
          >
            Upgrade to Pro
          </Link>
          <Link
            to="/analytics"
            style={{
              color: '#4f46e5',
              textDecoration: 'none',
              fontWeight: '500',
              fontSize: '1rem'
            }}
          >
            Back to Analytics
          </Link>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Tier Gated Route ---------------- */
const TierGatedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#4b5563",
          fontSize: "1rem",
        }}
      >
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has Pro tier or higher
  if (!isProOrHigher(user)) {
    return <GroupsPaywall />;
  }

  return children;
};

/* ---------------- Protected Route ---------------- */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth(); // ✅ use correct keys

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#4b5563",
          fontSize: "1rem",
        }}
      >
        Loading...
      </div>
    );
  }

  // ✅ Once loading is done, redirect only if user is null
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/* ---------------- Layout Wrappers ---------------- */
const MarketingLayout = ({ children }) => (
  <>
    <Navbar />
    <main className="page-shell">{children}</main>
    <Footer />
  </>
);

const AppLayout = ({ children }) => (
  <main className="app-shell">{children}</main>
);

/* ---------------- Router Setup ---------------- */
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          {/* Public marketing pages */}
          <Route
            path="/"
            element={
              <MarketingLayout>
                <Landing />
              </MarketingLayout>
            }
          />

          <Route
            path="/login"
            element={
              <MarketingLayout>
                <Login />
              </MarketingLayout>
            }
          />

          <Route
            path="/signup"
            element={
              <MarketingLayout>
                <Signup />
              </MarketingLayout>
            }
          />

          <Route
            path="/account/pricing"
            element={
              <MarketingLayout>
                <AccountPricing />
              </MarketingLayout>
            }
          />

          <Route
            path="/how-it-works"
            element={
              <MarketingLayout>
                <HowItWorks />
              </MarketingLayout>
            }
          />

          <Route
            path="/about"
            element={
              <MarketingLayout>
                <About />
              </MarketingLayout>
            }
          />

          <Route
            path="/methodology"
            element={
              <MarketingLayout>
                <Methodology />
              </MarketingLayout>
            }
          />

          <Route
            path="/faq"
            element={
              <MarketingLayout>
                <FAQ />
              </MarketingLayout>
            }
          />

          <Route
            path="/privacy"
            element={
              <MarketingLayout>
                <Privacy />
              </MarketingLayout>
            }
          />

          <Route
            path="/terms"
            element={
              <MarketingLayout>
                <Terms />
              </MarketingLayout>
            }
          />

          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/payment/success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment/cancel"
            element={
              <ProtectedRoute>
                <PaymentCancel />
              </ProtectedRoute>
            }
          />

          {/* Protected dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected analytics */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Analytics />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected groups */}
          <Route
            path="/groups"
            element={
              <TierGatedRoute>
                <AppLayout>
                  <Groups />
                </AppLayout>
              </TierGatedRoute>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
